import qs from 'query-string'
import $global from '@xt/client/store/global'
import { $service } from '@xt/client/experimental/request/service'
import { reportEntryLog } from '@xt/client/utils/log'
import lodash from 'lodash'
import type { LaunchMiniProgramOptions, WXOpenSDK } from '@xt/client/types/wxopensdk.d'
import { SDKErrorCode } from '@xt/client/enums/mini'

export type MiniProgrmType = 'uc' | 'show' | 'wenfang' | 'daycourse'

export type OpenMiniProgramOptionType = {
  miniProgrmType: MiniProgrmType
  path?: string
  query?: Record<string, any>
}

// 错误码描述映射
const SDKErrorMessages: Record<SDKErrorCode, string> = {
  [SDKErrorCode.SUCCESS]: '正常触发操作',
  [SDKErrorCode.INVALID_ENVIRONMENT]: '当前 sdk 运行环境不合法',
  [SDKErrorCode.NETWORK_ERROR]: '网络错误',
  [SDKErrorCode.TICKET_ERROR]: 'ticket 错误',
  [SDKErrorCode.INVALID_PARAMS]: '请求参数格式错误',
  [SDKErrorCode.SERVER_ERROR]: '后台异常',
  [SDKErrorCode.TIMEOUT]: '操作超时',
  [SDKErrorCode.UNKNOWN_ERROR]: '未知错误',
  [SDKErrorCode.RATE_LIMIT_EXCEEDED]: '超过频率限制',
  [SDKErrorCode.INVALID_API_NAME]: '接口名错误',
  [SDKErrorCode.TICKET_ERROR_V2]: 'ticket 错误',
  [SDKErrorCode.INVALID_DOMAIN]: '请求域名错误，请检查业务域名是否已在 open 登记',
  [SDKErrorCode.HTTPS_REQUIRED]: '需要 https 页面',
  [SDKErrorCode.PERMISSION_DENIED]: '当前接口无权限，请检查是否已在 open 开通相关功能权限',
  [SDKErrorCode.INVALID_REQUEST_METHOD]: '当前接口请求类型错误',
  [SDKErrorCode.CLIENT_VERSION_NOT_SUPPORTED]: '当前微信客户端版本不支持该接口'
}

const appids: Record<MiniProgrmType, string> = {
  show: 'gh_08398972343c', // 暄桐展览
  wenfang: 'gh_79c64ca9ccaf', // 暄桐文房
  daycourse: 'gh_6df4e6c6d0bc', // 暄桐日课
  uc: 'gh_2f751118256c' // 暄桐
}

declare global {
  interface Window {
    wxopensdk?: WXOpenSDK
  }
}

export type Result = {
  success: boolean
  reason: string
  errcodeDesc?: string
}

function ensureLeadingSlash(path: string): `/${string}` | '' {
  return typeof path !== 'string' || path === '' ? '' : path.startsWith('/') ? (path as `/${string}`) : `/${path}`
}

/**
 *
 * @param type 要打开的小程序
 * @param path 小程序启动路径
 * @param query 小程序页面参数
 * @param timeout 超时时间
 */
export async function openMiniProgram(
  type: MiniProgrmType,
  path: string,
  query: Record<string, any> = {},
  timeout: number = 5000
): Promise<Result> {
  const params = qs.stringify(query)

  if (ensureLeadingSlash(path) && params) {
    path += ensureLeadingSlash(path).indexOf('?') === -1 ? `?${params}` : `&=${params}`
  }

  console.warn(`小程序路径已经准备好: ${path}`)

  if ($global.platform !== 'PC') {
    reportEntryLog('system.open-mini.fail', { reason: '平台错误', platform: $global.platform, path })
    return {
      success: false,
      reason: '不支持的平台调用'
    }
  }

  if (!window.wxopensdk) {
    reportEntryLog('system.open-mini.fail', { reason: '找不到sdk实例', path })
    return {
      success: false,
      reason: '找不到sdk实例'
    }
  }

  // 获取本次接口需要的ticket
  const [err, ticket] = await $service<string>({
    url: `/uc/api/wxmp/opensdk/ticket`
  })

  if (err) {
    reportEntryLog('system.open-mini.get-ticket-fail', { err, path })
    return { success: false, reason: err.errMsg }
  }

  console.warn(`wxopensdk 是否ready: ${window.wxopensdk.ready} ticket = ${ticket}`)
  const options = { appid: $global.openAppid!, userName: appids[type], path, ticket, timeout } satisfies LaunchMiniProgramOptions

  if (window.wxopensdk.ready) {
    const { errcode, errmsg, actionId } = await window.wxopensdk.launchMiniProgram(options)

    console.log({
      errcode,
      errmsg,
      actionId,
      errcodeDesc: SDKErrorMessages[errcode]
    })

    if (errcode === 0) {
      return { success: true, reason: '' }
    }

    reportEntryLog('system.open-mini.fail', {
      errcode,
      errmsg,
      actionId,
      ...lodash.omit(options, 'ticket'),
      errcodeDesc: SDKErrorMessages[errcode]
    })
    return { success: false, reason: errmsg }
  }

  return new Promise<Result>(resolve => {
    window.wxopensdk!.onReady = async () => {
      const { errcode, errmsg, actionId } = await window.wxopensdk!.launchMiniProgram(options)

      if (errcode === 0) {
        resolve({ success: true, reason: '' })
      } else {
        reportEntryLog('system.open-mini.fail', {
          errcode,
          errmsg,
          actionId,
          pos: 'onReady',
          ...lodash.omit(options, 'ticket'),
          errcodeDesc: SDKErrorMessages[errcode]
        })
        resolve({ success: false, reason: errmsg })
      }
    }
  })
}
